/**
 * Sources-collection layer — the field/item module for the unlinked-sources
 * grid, parallel to `records.ts` / `listings.ts` / `times.ts`. Owns the
 * `SourceItem` shape and the grid's column fields, the single layer feeding an
 * unlinked-source collection its items + fields.
 *
 * Unlike `times.ts` / `links.ts`, the columns shown depend on the record type
 * being browsed (person sources surface an email + suggested-record matches;
 * organization / event sources surface a description). So fields are produced
 * by a `createSourceFields(recordType)` factory rather than a single static
 * array. Rows are external, unlinked objects — read-only display, no editing.
 */

import { type AugerField, type EnrichableField, getFieldType } from "../fields/fields.js"
import * as group from "../fields/group.js"
import { type BaseItem } from "./item.js"

/** A record suggested as a match for an unlinked source (resolved by email). */
export interface SuggestedRecord {
  id: number
  type: string
  name: string
  slug: string
}

/** An unlinked source item from an external system, as the sources grid reads
 *  it. `source_type` is the resolved/canonical type (e.g. "pipedrive_person");
 *  `source_key` is the raw query key it came back under (used by the source
 *  filters). Extra columns from the upstream query may ride along at run time;
 *  the type names only the ones code reads. */
export type SourceItem = BaseItem & {
  name: string
  email?: string
  description?: string
  url?: string
  source_type: string
  source_key: string
  source_id: string
  suggested_records: SuggestedRecord[]
}

const row = (item: unknown) => item as SourceItem

/** A `record_contacts` row from `batchSearchRecordsByContact.byEmail`. */
export interface EmailMatch {
  value: string
  record: SuggestedRecord | null
}

/**
 * Build the suggested-records map (item key → matches) for unlinked sources from
 * the batched email/name lookup. For each source: email matches rank first, then
 * name matches, deduplicated by record id so a record found both ways appears
 * once. Only sources with at least one match get an entry.
 *
 * Pure so the matching/dedupe rules can be tested in isolation; the
 * unlinked-sources view feeds it the query result and assigns the map.
 */
export function buildSuggestionsMap(
  items: SourceItem[],
  byEmail: EmailMatch[],
  byName: SuggestedRecord[]
): Map<string, SuggestedRecord[]> {
  // email value -> matched records (deduped by record id)
  const emailMatches = new Map<string, SuggestedRecord[]>()
  for (const match of byEmail) {
    if (!match.record) continue
    const list = emailMatches.get(match.value) ?? []
    if (!list.some((r) => r.id === match.record!.id)) list.push(match.record)
    emailMatches.set(match.value, list)
  }

  // record name -> matched records
  const nameMatches = new Map<string, SuggestedRecord[]>()
  for (const record of byName) {
    const list = nameMatches.get(record.name) ?? []
    list.push(record)
    nameMatches.set(record.name, list)
  }

  const result = new Map<string, SuggestedRecord[]>()
  for (const item of items) {
    const itemKey = `${item.source_type}-${item.source_id}`
    const suggestions: SuggestedRecord[] = []
    const seen = new Set<number>()

    const push = (record: SuggestedRecord) => {
      if (seen.has(record.id)) return
      seen.add(record.id)
      suggestions.push(record)
    }

    if (item.email) emailMatches.get(item.email)?.forEach(push)
    if (item.name) nameMatches.get(item.name)?.forEach(push)

    if (suggestions.length > 0) result.set(itemKey, suggestions)
  }

  return result
}

/**
 * Build the column fields for the sources grid, scoped to `recordType`.
 *
 * person:        Name · Email · Suggested Records · Has Matches · Source
 * organization / event:  Name · Description · Source
 *
 * All columns are read-only. The three with bespoke rendering — `name`
 * (links to the source detail), `suggested_records` (record chips) and
 * `source_key` (branded source icon) — are key-switched by the source field
 * cell; the rest fall through to the registry-resolved grid cell by `type`.
 */
export function createSourceFields(recordType: string): AugerField[] {
  const specs: EnrichableField[] = [
    {
      key: "name",
      label: "Name",
      type: "string",
      width: 250,
      value: (i) => row(i).name || "No name",
    },
    {
      key: "email",
      label: recordType === "person" ? "Email" : "Description",
      type: "string",
      width: 300,
      value: (i) =>
        recordType === "person" ? row(i).email || "-" : row(i).description || row(i).url || "-",
    },
  ]

  if (recordType === "person") {
    specs.push({
      key: "suggested_records",
      label: "Suggested Records",
      type: "record",
      isArray: true,
      width: 200,
      value: (i) => row(i).suggested_records || [],
    })
    specs.push({
      key: "has_matches",
      label: "Has Matches",
      type: "boolean",
      width: 120,
      value: (i) => (row(i).suggested_records || []).length > 0,
      // Group-header label for the grouped view.
      format: (value: unknown) => (value ? "Has Matches" : "No Matches"),
    })
  }

  specs.push({
    key: "source_key",
    label: "Source",
    type: "string",
    width: 120,
    ...group.byValue,
    value: (i) => row(i).source_key,
  })

  // Inline link affordance: one-click Link for a single match, a chip picker for
  // several, or a record autocomplete to link any record. Rendered bespoke by the
  // source field cell, which reads `item` directly; `value` exists only because the
  // field system requires it, mirroring `suggested_records`.
  specs.push({
    key: "link_action",
    label: "Link to",
    type: "string",
    width: 280,
    value: (i) => row(i).suggested_records || [],
  })

  // Enrich: resolve each spec's FieldType chain (so it carries that type's
  // format/parse/operators) and guarantee `rawValue` — read-only columns mirror
  // `value`, so `rawValue` defaults to it. Sources are external, unlinked rows:
  // every column is read-only (no inline editor opens, nothing persists).
  return specs.map(
    (f) =>
      ({
        ...getFieldType(f.type),
        ...f,
        isEditable: false,
        rawValue: f.rawValue ?? f.value,
      }) as AugerField
  )
}
